import { useState, useEffect, useCallback, useRef } from 'react';
import { useRestaurant } from '@core/tenant/TenantProvider';
import { env } from '@core/config/env';
import { captureMpSdkLoadOutcome } from '@core/sentry';
import {
  ensureMercadoPagoScript,
  removeMercadoPagoScript,
  isMpSdkLoaded,
  isMpSdkV2Enabled,
} from '@shared/utils/mercadoPagoSdkLoader';
import type { MercadoPagoV2Instance } from '@shared/utils/mercadoPagoSdkLoader';

/**
 * Raw card values for tokenization. Consumers keep the PAN in React state
 * (needed for the dual Stripe tokenization and encryptCardData) and pass it
 * here directly — the hidden `data-checkout` forms of the v1 era are gone
 * (issue #354). Digits only for cardNumber; expiration split as entered
 * (MM / YY), matching what the legacy hidden form used to submit.
 */
export interface MpCardTokenValues {
  holderName: string;
  cardNumber: string;
  securityCode: string;
  expirationMonth: string;
  expirationYear: string;
  docNumber: string;
  /** The document's type (documentTypes.ts); the legacy `Otro` when the venue collects none. */
  docType?: string;
}

// Legacy-compatible identification: every country we operate MP in
// (AR/CL/PE/MX) accepts docType 'Otro' with a random number. v2 sends the
// same pair as identificationType/identificationNumber.
const MP_DOC_TYPE = 'Otro';

interface MercadoPagoTokenResponse {
  id?: string;
  error?: string;
  // Array of { code, description } on validation failures, but MP's
  // bin-lookup path reports a single bare object instead.
  cause?: unknown;
}

/**
 * MP reports tokenization failures as `{ status, error, cause }` where
 * `cause` carries the only actionable detail — e.g.
 * `[{ code: '324', description: 'invalid parameter docNumber' }]` or
 * "payment method not found". Most rejections share the same opaque
 * `error: 'bad_request'`; without the cause codes in the message every
 * failure mode collapses into one undiagnosable Sentry issue (see
 * MEITRE-FRONTEND-V2-1M). The wire format is the same /v1/card_tokens body
 * under both SDKs — v1 passed it to the callback, v2 rejects with it — so
 * one formatter serves both paths.
 */
function formatMpTokenError(response: MercadoPagoTokenResponse): string {
  const base = response.error || 'Failed to create MercadoPago token';
  const causes = Array.isArray(response.cause)
    ? response.cause
    : response.cause
      ? [response.cause]
      : [];
  const detail = causes
    .map((cause) => {
      // The API body uses {code, description}; v2's client-side validation
      // rejects with {code, message} (verified against the live SDK).
      const { code, description, message } = (cause ?? {}) as {
        code?: string | number;
        description?: string;
        message?: string;
      };
      return [code, description ?? message]
        .filter((part) => part !== undefined && part !== '')
        .join(': ');
    })
    .filter(Boolean)
    .join('; ');
  return detail ? `${base} (${detail})` : base;
}

/**
 * Normalizes a v2 createCardToken rejection into an Error carrying the
 * cause codes. v2 rejects with the parsed API body (object with
 * error/message/cause), a bare cause array on some validation paths, or a
 * plain Error on network failures.
 */
function toMpTokenError(err: unknown): Error {
  if (err instanceof Error) return err;
  // v2's client-side field validation rejects with plain strings on some
  // paths (e.g. "invalid card number length") — those are the actionable
  // ones, don't collapse them into the generic message.
  if (typeof err === 'string' && err) return new Error(err);
  if (Array.isArray(err)) {
    return new Error(formatMpTokenError({ cause: err }));
  }
  if (err && typeof err === 'object') {
    const e = err as { error?: string; message?: string; cause?: unknown };
    return new Error(formatMpTokenError({ error: e.error || e.message, cause: e.cause }));
  }
  return new Error('Failed to create MercadoPago token');
}

/**
 * v1 rollback path only: the legacy SDK reads card data from
 * `data-checkout` inputs inside a form element. Build it detached and
 * in-memory — it never touches the DOM, so session-replay tools can't see
 * it and consumers don't need hidden forms in their JSX.
 */
function buildV1CheckoutForm(values: MpCardTokenValues): HTMLFormElement {
  const form = document.createElement('form');
  const fields: Array<[string, string]> = [
    ['cardholderName', values.holderName],
    ['cardNumber', values.cardNumber],
    ['securityCode', values.securityCode],
    ['cardExpirationMonth', values.expirationMonth],
    ['cardExpirationYear', values.expirationYear],
    ['docType', values.docType || MP_DOC_TYPE],
    ['docNumber', values.docNumber],
  ];
  for (const [name, value] of fields) {
    const input = document.createElement('input');
    input.setAttribute('data-checkout', name);
    input.value = value;
    form.appendChild(input);
  }
  return form;
}

const POLL_INTERVAL = 250;
// Per-attempt budget. Typical fetch failures (DNS, refused, blocked) fire
// onerror within a couple of seconds and retry immediately; the timeout only
// catches stalled connections, where a fresh request is the best recovery.
const ATTEMPT_TIMEOUT_MS = 8000;
const MAX_LOAD_ATTEMPTS = 3;
const RETRY_BASE_DELAY_MS = 500;

interface UseMercadoPagoSDKResult {
  isReady: boolean;
  error: string | null;
  createToken: (values: MpCardTokenValues) => Promise<string>;
  getPaymentMethod: (bin: string) => Promise<string>;
}

export function useMercadoPagoSDK(): UseMercadoPagoSDKResult {
  const restaurant = useRestaurant();
  const isMP = restaurant.paymentProcessor === 'mercadopago';
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // v2 instance for this tenant's key. Per-instance (not global like v1's
  // setPublishableKey) — cleanly scopes the marketplace-vs-restaurant key
  // selection to this hook's lifetime.
  const mpRef = useRef<MercadoPagoV2Instance | null>(null);

  const restaurantId = restaurant.id;

  useEffect(() => {
    if (!isMP) return;

    setIsReady(false);
    setError(null);

    let cancelled = false;
    let attempt = 0;
    let poll: ReturnType<typeof setInterval> | undefined;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    const succeed = () => {
      // The key the backend serves; the build-time marketplace key only when a
      // marketplace venue is served none.
      const key =
        restaurant.publicKeyPayment || (restaurant.hasMarketplace ? env.mpPublishableKey : '');
      if (!key) {
        // A missing publishable key must not flip isReady: the SDK would
        // "work" until createToken, then fail every attempt with an opaque
        // bad_request (the MEITRE-FRONTEND-V2-1M signature). Fail loud at
        // load time instead so the config problem is visible in Sentry.
        captureMpSdkLoadOutcome('failed', {
          restaurantId,
          attempts: attempt,
          reason: 'missing-key',
        });
        setError('MercadoPago publishable key missing');
        return;
      }
      if (isMpSdkV2Enabled()) {
        try {
          mpRef.current = new window.MercadoPago!(key, { advancedFraudPrevention: true });
        } catch {
          // v1's setPublishableKey was an inert assignment; the v2
          // constructor throws (malformed key, AFP bootstrap failure). A
          // throw here would escape the effect on the preloaded path (no
          // error boundary covers the payment routes → blank page) or get
          // swallowed inside the poll callback (settled/clearInterval
          // already ran → eternal spinner with no telemetry). Fail loud
          // like the missing-key branch instead.
          captureMpSdkLoadOutcome('failed', {
            restaurantId,
            attempts: attempt,
            reason: 'init-error',
          });
          setError('MercadoPago SDK failed to initialize');
          return;
        }
      } else {
        window.Mercadopago!.setPublishableKey(key);
      }
      if (attempt > 1) {
        // A retry saved this session — report it so the volume/geography of
        // flaky SDK loads is measurable in Sentry.
        captureMpSdkLoadOutcome('recovered', { restaurantId, attempts: attempt });
      }
      setIsReady(true);
    };

    const startAttempt = () => {
      if (cancelled) return;
      attempt += 1;

      // The SDK assigns its global synchronously on eval, so a defined
      // global means the script (this attempt's or a preloaded one)
      // already executed.
      if (isMpSdkLoaded()) {
        succeed();
        return;
      }

      const script = ensureMercadoPagoScript(attempt);

      // One shot per attempt: onerror and the stall timeout can both fire
      // for the same script, and failAttempt must not double-schedule.
      let settled = false;
      const failAttempt = (reason: 'script-error' | 'timeout') => {
        if (cancelled || settled) return;
        settled = true;
        if (poll) clearInterval(poll);
        // Drop the dead (or stalled) tag so the next attempt injects a
        // fresh request instead of polling a script that will never run.
        removeMercadoPagoScript();
        if (attempt < MAX_LOAD_ATTEMPTS) {
          retryTimer = setTimeout(startAttempt, RETRY_BASE_DELAY_MS * 2 ** (attempt - 1));
        } else {
          captureMpSdkLoadOutcome('failed', { restaurantId, attempts: attempt, reason });
          setError('MercadoPago SDK failed to load');
        }
      };

      script.addEventListener('error', () => failAttempt('script-error'), { once: true });

      const startTime = Date.now();
      poll = setInterval(() => {
        if (isMpSdkLoaded()) {
          if (settled) return;
          settled = true;
          clearInterval(poll);
          succeed();
        } else if (Date.now() - startTime > ATTEMPT_TIMEOUT_MS) {
          failAttempt('timeout');
        }
      }, POLL_INTERVAL);
    };

    startAttempt();

    return () => {
      cancelled = true;
      if (poll) clearInterval(poll);
      if (retryTimer) clearTimeout(retryTimer);
      mpRef.current = null;
      setIsReady(false);
      setError(null);
    };
  }, [isMP, restaurantId, restaurant.hasMarketplace, restaurant.publicKeyPayment]);

  const createToken = useCallback(async (values: MpCardTokenValues): Promise<string> => {
    if (isMpSdkV2Enabled()) {
      const mp = mpRef.current;
      // Distinct from the v1 'not loaded' message: the script may have
      // loaded fine and initialization bailed (missing key, constructor
      // throw) — collapsing both into one string misdirects Sentry triage.
      if (!mp) throw new Error('MercadoPago SDK not initialized');
      // Core Methods document a 4-digit cardExpirationYear; consumers
      // validate MM/YY, and the closed-source v2 bundle may not normalize
      // a 2-digit year the way the v1 bundle did. The v1 rollback path
      // keeps YY — that's what the legacy bundle received in prod.
      const expirationYear =
        values.expirationYear.length === 2
          ? `20${values.expirationYear}`
          : values.expirationYear;
      let token: { id: string };
      try {
        token = await mp.createCardToken({
          cardNumber: values.cardNumber,
          cardholderName: values.holderName,
          cardExpirationMonth: values.expirationMonth,
          cardExpirationYear: expirationYear,
          securityCode: values.securityCode,
          identificationType: values.docType || MP_DOC_TYPE,
          identificationNumber: values.docNumber,
        });
      } catch (err) {
        throw toMpTokenError(err);
      }
      if (!token?.id) throw new Error('Failed to create MercadoPago token');
      return token.id;
    }

    // v1 rollback path: callback API over a detached data-checkout form.
    return new Promise((resolve, reject) => {
      if (!window.Mercadopago) {
        reject(new Error('MercadoPago SDK not loaded'));
        return;
      }
      window.Mercadopago.clearSession();
      window.Mercadopago.createToken(buildV1CheckoutForm(values), (status, response) => {
        if ((status === 200 || status === 201) && response.id) {
          resolve(response.id);
        } else {
          reject(new Error(formatMpTokenError(response)));
        }
      });
    });
  }, []);

  const getPaymentMethod = useCallback(async (bin: string): Promise<string> => {
    if (isMpSdkV2Enabled()) {
      const mp = mpRef.current;
      if (!mp) throw new Error('MercadoPago SDK not initialized');
      const { results } = await mp.getPaymentMethods({ bin });
      if (!results || results.length === 0) {
        throw new Error('Failed to detect payment method');
      }
      return results[0].id;
    }

    return new Promise((resolve, reject) => {
      if (!window.Mercadopago) {
        reject(new Error('MercadoPago SDK not loaded'));
        return;
      }
      window.Mercadopago.getPaymentMethod({ bin }, (status, response) => {
        if (status === 200 && response.length > 0) {
          resolve(response[0].id);
        } else {
          reject(new Error('Failed to detect payment method'));
        }
      });
    });
  }, []);

  return { isReady, error, createToken, getPaymentMethod };
}
